import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { handleLibraryRequest } from '../utils/backendServices';
import UploadStateModal from '../components/UploadStateModal';
import { useLibraryContext } from '../contexts/LibraryContext';
import Spinner from '../components/Spinner';
import {
    UploadProgress,
    UploadProgressContext,
} from '../contexts/UploadProgressContext';
import { useInterval } from '../hooks/useInterval';
import { useUploadProgress } from '../hooks/useUploadProgress';
import Library from './Library';
import AssetDetails from '../components/AssetDetails';
import { freeAccess } from '../providers/AuthProvider';
import { useAuth } from '@clerk/clerk-react';
import OrganizationSyncProvider from '../providers/OrganizationSyncProvider';
import NavigateWithinOrg from '../utils/navigation';
import FeatureNotAvailableModal from '../components/FeatureNotAvailableModal';
import { useModalContext } from '../contexts/ModalContext';
import { useLatestSuccessfulUpload } from '../contexts/UploadContext';
import Analytics from './Analytics';
import { MacScrollbar } from 'mac-scrollbar';
import styled from 'styled-components';

const Scrollbar = styled(MacScrollbar)`
    flex: 1; /* Take up remaining space */
    overflow-y: auto; /* Enable scrolling */
    width: 100%;
    height: calc(100vh - 50px);
`;

const MainApp = () => {
    const { orgId } = useAuth();

    const [firstRequestSuccessful, setFirstRequestSuccessful] =
        useState<boolean>(false);

    const libraryContext = useLibraryContext();
    const { modalText, setModalText } = useModalContext();

    const latestSuccessfulUpload = useLatestSuccessfulUpload();
    const location = useLocation();
    const scrollbarRef = useRef<HTMLElement | null>(null);
    const scrollPositionsRef = useRef<Record<string, number>>({});
    const assignScrollbarRef = useCallback((node: HTMLElement | null) => {
        scrollbarRef.current = node;
    }, []);

    const getLocationKey = (loc: {
        pathname: string;
        search: string;
        hash: string;
    }) => `${loc.pathname}${loc.search}${loc.hash}`;

    useLayoutEffect(() => {
        const storedScrollTop = scrollPositionsRef.current[getLocationKey(location)];

        requestAnimationFrame(() => {
            if (!scrollbarRef.current) {
                return;
            }
            scrollbarRef.current.scrollTop =
                typeof storedScrollTop === 'number' ? storedScrollTop : 0;
        });
    }, [location]);
    // todo: should be moved into Library Component

    useEffect(() => {
        // if (organization) {
        handleLibraryRequest(libraryContext)
            .then(() => {
                setFirstRequestSuccessful(true);
            })
            .catch((error) => {
                console.log(error);
            });
        //
    }, [
        libraryContext.state.appliedFilters,
        libraryContext.state.searchString,
        libraryContext.state.appliedSortOption,
        orgId,
        latestSuccessfulUpload,
    ]);

    useInterval(() => {
        handleLibraryRequest(
            libraryContext,
            libraryContext.state.currentPage,
            false,
        ).catch((error) => {
            console.log(error);
        });
    }, 60000);

    const [uploadProgressArray, setUploadProgressArray] = useState<
        UploadProgress[]
    >([]);
    const uploadProgressArrayRef = useRef<UploadProgress[]>([]);
    uploadProgressArrayRef.current = uploadProgressArray;

    if (!freeAccess) {
        useUploadProgress(uploadProgressArrayRef, setUploadProgressArray);
    }

    const routes = [
        { path: '/library', element: <Library /> },
        { path: '/analytics', element: <Analytics /> },
        {
            path: '/library/:id',
            element: <AssetDetails />,
        },
    ];

    return (
        <>
            <OrganizationSyncProvider />
            {!firstRequestSuccessful ? (
                <Spinner />
            ) : (
                <>
                    <FeatureNotAvailableModal
                        modalText={modalText}
                        onClose={() => setModalText(null)}
                    />
                    <NavBar />
                    <div className="App">
                        <Scrollbar
                            ref={assignScrollbarRef as unknown as (node: any) => void}
                            onScroll={(e) => {
                                scrollPositionsRef.current[
                                    getLocationKey(location)
                                ] = (e.target as HTMLElement).scrollTop;
                            }}
                            trackStyle={(horizontal) => ({
                                [horizontal ? 'height' : 'width']: 0,
                                right: 0,
                                border: 0,
                            })}
                            thumbStyle={(horizontal) => ({
                                [horizontal ? 'height' : 'width']: 6,
                            })}
                        >
                            <UploadProgressContext.Provider
                                value={{
                                    uploadProgressArray,
                                    setUploadProgressArray,
                                    uploadProgressArrayRef,
                                }}
                            >
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            <NavigateWithinOrg
                                                to={'/library'}
                                            />
                                        }
                                    />
                                    {routes.map((route) => {
                                        return (
                                            <Route
                                                key={route.path}
                                                path={route.path}
                                                element={route.element}
                                            />
                                        );
                                    })}
                                </Routes>
                                <UploadStateModal />
                            </UploadProgressContext.Provider>
                        </Scrollbar>
                    </div>
                </>
            )}
        </>
    );
};
export default MainApp;
